import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import { arrow, autoUpdate, flip, FloatingArrow, FloatingFocusManager, offset, shift, size, useClick, useDelayGroup, useDismiss, useFloating, useFocus, useHover, useInteractions, useMergeRefs, useRole, useTransitionStatus, } from '@floating-ui/react';
import stylex from '@stylexjs/stylex';
import { isFunction } from '../../helpers/isFunction';
import { useControlledValue } from '../../hooks/useControlledValue';
import { usePopoverCursor } from '../../hooks/usePopoverCursor';
import { useStyles } from '../../hooks/useStyles';
import { isObject } from '../../helpers/isObject';
import { commonStyles } from '../../helpers/commonStyles';
import { fixedForwardRef } from '../../helpers/fixedForwardRef';
import { FloatingTransition } from '../FloatingTransition';
import { Scrim } from '../Scrim';
import { Portal } from '../Portal';
import { popoverBaseStyles } from './PopoverBase.styles';
import { PreventAutoFocus } from './PreventAutoFocus';
const defaultOpenEvents = {
    hover: false,
    focus: false,
    click: false,
    touch: false,
};
const defaultCloseEvents = {
    clickOutside: true,
    focusOut: true,
    escapeKey: true,
};
const defaultMiddlewares = {
    shift: true,
    flip: true,
    size: true,
};
export const PopoverBase = fixedForwardRef(function PopoverBase(props, forwardedRef) {
    const { styles, sx, root, contentRenderer, children, placement = 'top', transitionOrientation, transitionOrigin: transitionOriginProp, opened: openedProp, defaultOpened, cursor: cursorType = false, onOpen, onClose, forwardProps, disabled, role: roleProp, trapFocus, matchTargetWidth, withScrim, slotProps, middlewares: middlewaresProp, additionalMiddlewares, additionalInteractions, floatingStrategy = 'absolute', openEvents: openEventsProp, closeEvents: closeEventsProp, preventAutoFocus, ...other } = props;
    const openEvents = {
        ...defaultOpenEvents,
        ...openEventsProp,
    };
    const closeEvents = {
        ...defaultCloseEvents,
        ...closeEventsProp,
    };
    const middlewares = {
        ...defaultMiddlewares,
        ...middlewaresProp,
    };
    const transitionOrigin = transitionOriginProp ?? (cursorType ? 'cursor' : 'corner');
    const { getStyles, globalStyles } = useStyles({
        name: 'PopoverBase',
        styles: [popoverBaseStyles, styles],
    });
    const [opened, setOpened] = useControlledValue({
        controlled: openedProp,
        default: defaultOpened || false,
        name: 'PopoverBase',
        onValueChange: (newOpened) => {
            if (newOpened !== opened) {
                if (newOpened) {
                    onOpen?.();
                }
                else {
                    onClose?.();
                }
            }
        },
    });
    const arrowRef = useRef(null);
    const cursor = usePopoverCursor({ type: cursorType });
    const floating = useFloating({
        placement,
        open: opened,
        onOpenChange: setOpened,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(cursorType ? 4 + cursor.size.height : undefined),
            middlewares.flip &&
                flip({
                    crossAxis: placement.includes('-'),
                    fallbackAxisSideDirection: 'start',
                    padding: 5,
                    ...(isObject(middlewares.flip) ? middlewares.flip : undefined),
                }),
            middlewares.shift &&
                shift({
                    padding: 8,
                    ...(isObject(middlewares.shift) ? middlewares.shift : undefined),
                }),
            !!cursorType &&
                arrow({
                    element: arrowRef,
                }),
            middlewares.size &&
                size({
                    apply: matchTargetWidth
                        ? ({ rects, elements }) => {
                            Object.assign(elements.floating.style, {
                                width: `${rects.reference.width}px`,
                            });
                        }
                        : undefined,
                    ...(isObject(middlewares.size) ? middlewares.size : undefined),
                }),
            ...(additionalMiddlewares ?? []),
        ],
    });
    const delayGroup = useDelayGroup(floating.context, {
        id: openEvents.hover ? undefined : '__persistent',
    });
    const hover = useHover(floating.context, {
        enabled: !!children && !!openEvents.hover && !disabled,
        move: false,
        delay: delayGroup.delay,
        mouseOnly: !openEvents.touch,
    });
    const click = useClick(floating.context, {
        enabled: !!children && !!openEvents.click && !disabled,
    });
    const focus = useFocus(floating.context, {
        enabled: !!children && !!openEvents.focus && !disabled,
        visibleOnly: true,
    });
    const dismiss = useDismiss(floating.context, {
        outsidePress: !!closeEvents.clickOutside,
        escapeKey: closeEvents.escapeKey,
    });
    const role = useRole(floating.context, { role: roleProp });
    const interactions = useInteractions([
        hover,
        focus,
        click,
        dismiss,
        role,
        ...(additionalInteractions ?? []),
    ]);
    const transitionStatus = useTransitionStatus(floating.context, {
        duration: 150, // motionTokens.duration$short3
    });
    const renderCursor = (userProps) => (_jsx(FloatingArrow, { ...userProps, ref: arrowRef, context: floating.context, width: cursor.size.width, height: cursor.size.height, d: cursor.svgPath }));
    const triggerElement = isFunction(children)
        ? children({
            opened,
            placement: floating.placement,
            getProps: interactions.getReferenceProps,
            setRef: floating.refs.setReference,
            close: () => setOpened(false),
        })
        : children;
    const floatingHandleRef = useMergeRefs([
        floating.refs.setFloating,
        forwardedRef,
    ]);
    return (_jsxs(_Fragment, { children: [triggerElement, transitionStatus.isMounted ? (_jsx(Portal, { root: root, children: _jsx(FloatingFocusManager, { disabled: !trapFocus, context: floating.context, modal: true, closeOnFocusOut: closeEvents.focusOut, ...slotProps?.floatingFocusManager, children: _jsxs("div", { ...stylex.props(globalStyles, sx), children: [withScrim ? (_jsx(Scrim, { floatingContext: floating.context, lockScroll: true, ...slotProps?.scrim })) : null, _jsx("div", { ...getStyles('floating', floatingStrategy && `floating$${floatingStrategy}`, floatingStrategy &&
                                    commonStyles.position(floating.x, floating.y)), ref: floatingHandleRef, ...interactions.getFloatingProps(), children: _jsxs(FloatingTransition, { placement: floating.placement, status: transitionStatus.status, origin: transitionOrigin, cursorTransformOrigin: cursor.getTransformOrigin(floating), orientation: transitionOrientation, pattern: floatingStrategy ? 'enterExit' : 'enterExitOffScreen', ...slotProps?.floatingTransition, children: [preventAutoFocus ? _jsx(PreventAutoFocus, {}) : null, isFunction(contentRenderer) ? (contentRenderer({
                                            placement: floating.placement,
                                            close: () => setOpened(false),
                                            forwardedProps: forwardProps
                                                ? other
                                                : undefined,
                                            renderCursor,
                                        })) : (_jsxs(_Fragment, { children: [renderCursor(), contentRenderer] }))] }) })] }) }) })) : null] }));
});
